import {Injectable} from "@angular/core";
import {BehaviorSubject, map, Observable, tap} from "rxjs";
import {Email} from "../model/email.model";
import {HttpClient} from "@angular/common/http";
import {
  ApplicationDto,
  EmitApplicationStatusEventCommand,
  EmitNewEligibleLotteryEventsCommand,
  RetryFailedApplicationsResponse,
  UpsertApplicationCommand
} from "admin-api-dto";
import {environment} from "../environments/environment";

@Injectable({
  providedIn: 'root'
})
export class ApplicationService {
  get failedApplications$(): Observable<ApplicationDto[]> {
    return this._failedApplications$.asObservable();
  }

  private readonly _failedApplications$ = new BehaviorSubject<ApplicationDto[]>([]);

  constructor(private readonly http: HttpClient) {
  }

  public retrieveApplications(): Observable<unknown> {
    return this.http.get<ApplicationDto[]>(`${environment.apiUrl}/api/private/applications`, {params: {status: 'failed'}}).pipe(
      tap(applications => this._failedApplications$.next(applications
        .sort(
          (a, b) => new Date(b.createdAt ?? new Date().getTime()).getTime() - new Date(a.createdAt ?? new Date().getTime()).getTime()
        ))));
  }

  public getDelayedApplications(): Observable<ApplicationDto[]> {
    return this.http.get<ApplicationDto[]>(`${environment.apiUrl}/api/private/applications/expired`).pipe(
      map(applications => applications
        .sort(
          (a, b) => new Date(b.createdAt ?? new Date().getTime()).getTime() - new Date(a.createdAt ?? new Date().getTime()).getTime()
        ))
    );
  }

  public deleteApplication(id: string) {
    return this.http.delete(`${environment.apiUrl}/api/private/applications/${id}`);
  }

  public resyncApplication(id: string): Observable<unknown> {
    return this.http.put(`${environment.apiUrl}/api/private/applications/${id}/resync`, {});
  }

  public retryAllFailedApplications(): Observable<RetryFailedApplicationsResponse> {
    return this.http.put<RetryFailedApplicationsResponse>(`${environment.apiUrl}/api/private/applications/retry-failed-applications`, {});
  }

  public markApplicationAsNotSelected(id: string): Observable<unknown> {
    return this.http.put(`${environment.apiUrl}/api/private/applications/${id}/mark-not-selected`, {});
  }

  public upsertApplicationForUser(userId: string, command: UpsertApplicationCommand): Observable<ApplicationDto> {
    return this.http.post<ApplicationDto>(`${environment.apiUrl}/api/private/users/${userId}/applications`, command);
  }

  public emitApplicationStatusEvent(id: string, command: EmitApplicationStatusEventCommand): Observable<{ status: string }> {
    return this.http.post<{ status: string }>(`${environment.apiUrl}/api/private/applications/${id}/status-event`, command);
  }

  public emitNewEligibleLotteryEvents(userId: string, command: EmitNewEligibleLotteryEventsCommand): Observable<{ status: string; count: number }> {
    return this.http.post<{ status: string; count: number }>(`${environment.apiUrl}/api/private/users/${userId}/new-eligible-lottery-events`, command);
  }
}
